import {
  defaultOperators,
  Tokenizer,
  TypeGuards,
  type TagToken,
  // biome-ignore lint/style/noRestrictedImports: tokenizer-only import; the lib wrapper imports this module, so importing the wrapper here would create a cycle
} from "liquidjs";

/**
 * Security checks for user-authored LiquidJS templates.
 *
 * Blocks template strings that contain the seed pattern of the LiquidJS v9
 * prototype-pollution → RCE chain (Dependabot alert #1819):
 *
 *   `{{ 1 | valueOf }}` returns the JS Number prototype on v9, which lets an
 *   attacker chain `| constructor` and friends to escape the template sandbox
 *   and execute arbitrary code.
 *
 * A production scan (`packages/scripts/scan-liquid-valueof-rce.sql`) against
 * every prod table that stores user-authored Liquid (1.1B+ historical
 * sync_requests included) returned zero hits, so blocking this pattern at
 * save time breaks no existing customer template.
 *
 * This module is pure (no I/O, no logger) so it can be imported from both
 * the backend renderer and the frontend save-time validator. Backend callers
 * that want loud telemetry should catch `LiquidSecurityError` and report it
 * via their own logger / Sentry path.
 */

/**
 * Pipe followed by optional whitespace and the literal identifier `valueOf`.
 * This is the seed of the v9 RCE chain. Case-sensitive — the JS identifier
 * is `valueOf` and Liquid filter names are case-sensitive, so a case-insensitive
 * match would only produce false positives on benign substrings like `valueof_id`.
 */
export const LIQUID_RCE_VALUEOF_PATTERN = /\|\s*valueOf\b/;

/**
 * Error thrown when a template contains a forbidden security pattern.
 *
 * Tagged with `name = "LiquidSecurityError"` so callers (GraphQL error
 * formatters, Express middleware) can identify it across rethrow boundaries
 * without instanceof checks failing under bundler duplication.
 */
export class LiquidSecurityError extends Error {
  override readonly name = "LiquidSecurityError";
  declare readonly pattern: string;
  declare readonly snippet: string;

  constructor(pattern: string, snippet: string) {
    // Message is intentionally vague — anything that surfaces to the user
    // must not name the offending pattern, since that would teach an
    // attacker what to try next. Forensic detail lives in `pattern` and
    // `snippet` for logger/Sentry capture.
    super(
      "This template was rejected. Please contact support for more information.",
    );
    Object.defineProperty(this, "pattern", {
      value: pattern,
      enumerable: false,
    });
    Object.defineProperty(this, "snippet", {
      value: snippet,
      enumerable: false,
    });
  }
}

/**
 * Returns true if the template contains the `| valueOf` RCE seed pattern.
 */
export function containsLiquidRcePattern(template: string): boolean {
  return LIQUID_RCE_VALUEOF_PATTERN.test(template);
}

const SNIPPET_CONTEXT_CHARS = 40;
const SNIPPET_TOKEN_SLACK_CHARS = 64;

const isWhitespace = (char: string | undefined): boolean =>
  char !== undefined && /\s/.test(char);

// Widen to the nearest whitespace within the slack so a short token such as
// an email is not split, which would let it past PII redaction.
function snippetAround(
  template: string,
  index: number,
  length: number,
): string {
  let start = Math.max(0, index - SNIPPET_CONTEXT_CHARS);
  const startFloor = Math.max(0, start - SNIPPET_TOKEN_SLACK_CHARS);
  while (start > startFloor && !isWhitespace(template[start - 1])) start--;

  let end = Math.min(template.length, index + length + SNIPPET_CONTEXT_CHARS);
  const endCeiling = Math.min(template.length, end + SNIPPET_TOKEN_SLACK_CHARS);
  while (end < endCeiling && !isWhitespace(template[end])) end++;

  return template.slice(start, end);
}

/**
 * Throws `LiquidSecurityError` if the template contains a forbidden pattern.
 *
 * Pure — does not log. Backend callers should catch and report via their
 * own logger / Sentry path so the alert lands in Datadog with the right tags.
 */
export function assertLiquidTemplateIsSafe(template: string): void {
  const match = template.match(LIQUID_RCE_VALUEOF_PATTERN);
  if (match) {
    throw new LiquidSecurityError(
      LIQUID_RCE_VALUEOF_PATTERN.source,
      snippetAround(template, match.index ?? 0, match[0].length),
    );
  }
}

export type BannedLiquidPattern = {
  readonly name: string;
  readonly pattern: RegExp;
};

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const PROTOTYPE_CHAIN_IDENTIFIERS = [
  ...new Set([...Object.getOwnPropertyNames(Object.prototype), "prototype"]),
];

export const LIQUID_BANNED_PATTERNS: readonly BannedLiquidPattern[] =
  PROTOTYPE_CHAIN_IDENTIFIERS.map((name) => ({
    name,
    pattern: new RegExp(`\\b${escapeRegExp(name)}\\b`),
  }));

export type BannedLiquidPatternMatch = {
  readonly name: string;
  readonly patternSource: string;
  readonly snippet: string;
};

const SCAN_MEMO_MAX_ENTRIES = 500;
const SCAN_MEMO_MAX_KEY_BYTES = 4_000_000;
const SCAN_MEMO_MAX_SINGLE_KEY_BYTES = 200_000;

const scanMemo = new Map<string, BannedLiquidPatternMatch | null>();
let scanMemoKeyBytes = 0;

function memoizeScanResult(
  template: string,
  result: BannedLiquidPatternMatch | null,
): void {
  if (template.length > SCAN_MEMO_MAX_SINGLE_KEY_BYTES) return;
  while (
    scanMemo.size >= SCAN_MEMO_MAX_ENTRIES ||
    (scanMemoKeyBytes + template.length > SCAN_MEMO_MAX_KEY_BYTES &&
      scanMemo.size > 0)
  ) {
    const oldest = scanMemo.keys().next();
    if (oldest.done) break;
    scanMemoKeyBytes -= oldest.value.length;
    scanMemo.delete(oldest.value);
  }
  if (!scanMemo.has(template)) scanMemoKeyBytes += template.length;
  scanMemo.set(template, result);
}

function findBannedPatternInRegion(
  template: string,
  regionText: string,
  regionOffset: number,
): BannedLiquidPatternMatch | undefined {
  for (const banned of LIQUID_BANNED_PATTERNS) {
    const match = regionText.match(banned.pattern);
    if (match) {
      const at = regionOffset + (match.index ?? 0);
      return {
        name: banned.name,
        patternSource: banned.pattern.source,
        snippet: snippetAround(template, at, match[0].length),
      };
    }
  }
  return undefined;
}

function findBannedPatternInLiquidTag(
  template: string,
  token: TagToken,
): BannedLiquidPatternMatch | undefined {
  if (token.name !== "liquid") {
    return findBannedPatternInRegion(template, token.getText(), token.begin);
  }

  const tagText = token.getText();
  const contentOffset = tagText.indexOf(token.content);
  const argsOffsetInContent = token.content.indexOf(
    token.args,
    token.name.length,
  );
  if (contentOffset === -1 || argsOffsetInContent === -1) return undefined;
  const argsOffset = contentOffset + argsOffsetInContent;

  const statements = new Tokenizer(
    token.args,
    defaultOperators,
  ).readLiquidTagTokens();
  for (const statement of statements) {
    if (statement.name === "#") continue;
    const match = findBannedPatternInRegion(
      template,
      statement.getText(),
      token.begin + argsOffset + statement.begin,
    );
    if (match) return match;
  }
  return undefined;
}

export function findBannedLiquidPattern(
  template: string,
): BannedLiquidPatternMatch | undefined {
  const memoized = scanMemo.get(template);
  if (memoized !== undefined) return memoized ?? undefined;

  let result: BannedLiquidPatternMatch | null = null;
  try {
    const tokens = new Tokenizer(
      template,
      defaultOperators,
    ).readTopLevelTokens();
    let inComment = false;
    for (const token of tokens) {
      if (TypeGuards.isTagToken(token)) {
        if (token.name === "comment" && !inComment) {
          inComment = true;
          continue;
        }
        if (token.name === "endcomment" && inComment) {
          inComment = false;
          continue;
        }
        if (inComment || token.name === "#") continue;
        result = findBannedPatternInLiquidTag(template, token) ?? null;
      } else if (TypeGuards.isOutputToken(token) && !inComment) {
        result =
          findBannedPatternInRegion(template, token.getText(), token.begin) ??
          null;
      }
      if (result) break;
    }
  } catch {
    // A template the tokenizer rejects has no executable syntax to scan.
  }
  memoizeScanResult(template, result);
  return result ?? undefined;
}

export type LiquidBannedPatternDisposition = "blocked" | "observed";

/** The chokepoint that ran the gate: wrapper-engine parse, an sdk render
 * helper, or a mapping compile. */
export type LiquidBannedPatternGateSite =
  | "parse"
  | "render"
  | "render-async"
  | "json-template"
  | "mapping"
  | "save";

export type LiquidBannedPatternGateHooks = {
  shouldBlock: () => boolean;
  report: (detection: {
    match: BannedLiquidPatternMatch;
    disposition: LiquidBannedPatternDisposition;
    site: LiquidBannedPatternGateSite;
    template: string;
  }) => void;
};

let bannedPatternGateHooks: LiquidBannedPatternGateHooks | undefined;

export function setLiquidBannedPatternGateHooks(
  hooks: LiquidBannedPatternGateHooks | undefined,
): void {
  bannedPatternGateHooks = hooks;
}

export function runLiquidBannedPatternGate(
  template: string,
  site: LiquidBannedPatternGateSite,
): void {
  if (!bannedPatternGateHooks) return;
  const match = findBannedLiquidPattern(template);
  if (!match) return;

  const disposition: LiquidBannedPatternDisposition =
    bannedPatternGateHooks.shouldBlock() ? "blocked" : "observed";
  bannedPatternGateHooks.report({ match, disposition, site, template });
  if (disposition === "blocked") {
    throw new LiquidSecurityError(match.patternSource, match.snippet);
  }
}

const LIQUID_SYNTAX_RE = /\{\{|\{%/;

/** Strings with Liquid syntax in a JSON-like value, with paths. */
export function collectLiquidStrings(
  value: unknown,
  path = "",
): Array<{ path: string; template: string }> {
  if (typeof value === "string") {
    return LIQUID_SYNTAX_RE.test(value) ? [{ path, template: value }] : [];
  }
  if (value === null || typeof value !== "object") return [];
  if (Array.isArray(value)) {
    return value.flatMap((item, i) =>
      collectLiquidStrings(item, `${path}[${i}]`),
    );
  }
  return Object.entries(value).flatMap(([key, child]) =>
    collectLiquidStrings(child, path ? `${path}.${key}` : key),
  );
}

/** Runs the gate over every Liquid string in a value. */
export function runLiquidBannedPatternGateOnValue(
  value: unknown,
  site: LiquidBannedPatternGateSite,
): void {
  if (!bannedPatternGateHooks) return;
  for (const { template } of collectLiquidStrings(value)) {
    runLiquidBannedPatternGate(template, site);
  }
}
